import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import LoadingBar from 'react-redux-loading-bar';
import classnames from 'classnames';
import get from 'lodash/get';
import compose from 'recompose/compose';
import withProps from 'recompose/withProps';
import { t } from '../modules/infra/i18n';

import { EventEmitter } from 'events';

import {
  getFirstLogin,
  getRoleIdType,
  getFrontendFlags,
  canActiveShopUseMemberships,
  getShopBrandId,
} from './auth/selectors';
import withSidebarStateAndContext from './common/hoc/withSidebarStateAndContext';
import { ConciergeChat, getConciergeManager } from '../modules/shared/concierge';
import FirstLoginWelcome from './help/FirstLoginWelcome';
import withLoansSidebarButtons from './loans/withLoansSidebarButtons';
import { getAppointment } from './main/appointments/actions';
import Main from './main/main';
import Sidebar from './sidebar/sidebar';
import { getBarbers, barberUpdated } from './main/barbers/actions';
import { subscribe, unsubscribe } from './common/pubnub';
import { GLYPHS } from './common/icon';
import HeaderShopsContainer from './sidebar/header/shopsContainer';
import withCollapsed from './sidebar/withCollapsed';
import {
  canUserViewReports,
  canUserViewCustomers,
  canUserViewServices,
  canUserViewBarbers,
  canUserViewEngage,
  canUserViewProducts,
  canUserViewRegister,
  canUseEngage,
  canUserViewDashboard,
  canUserViewStaffManagement,
} from './common/helpers/users';
import { getShopBankAccount, getShopMerchantInfo } from './main/settings/actions';
import { fetchShopActiveRegister } from './register/cashRegister/actions';
import { fetchShopDiscounts, fetchBarberDiscounts } from './main/settings/discounts/actions';
import {
  shopFetchWaitingListCustomers,
  barberFetchWaitingListCustomers,
} from './main/waitingList/waitingListCustomers/actions';
import * as loyaltyActions from './main/settings/loyalty/actions';
import { containerApp } from '../main';
import { getJobsStatus } from './queue/actions';
import shopAppFetchTimer from './main/timeTracker/hoc/shopAppFetchTimer';
import withTimeTrackingSync from './main/timeTracker/hoc/withTimeTrackingSync';
import HandoffListener from './handoff/handoffListener';
import HandoffStatusBar from './handoff/StatusBar';
import RegisterButton from './common/RegisterButton';
import FixedButtonsContainer from './common/FixedButtonsContainer';
import ChatButton from './chat/ChatButton';
import ChatPanel from './chat/ChatPanel';
import withIsChatOpen from './chat/withIsChatOpen';
import withChatUnreadMessagesCount from './chat/withChatUnreadMessagesCount';
import { initializeEcommerceMatches } from './products/products/Shopify/reducer/actions';
import { fetchShopCampaign } from './engagement/actions';
import { Onboarding } from '../modules/shop/onboarding';
import { withClientReferralsNavButton } from '../modules/shop/client-referrals/with-client-reverrals-nav-button';
import { ShopViewPopups } from '../modules/shared/app-start-popups/shop-view-popups';
import { withFeatureFlags } from '../modules/infra/feature-flags/hoc/with-feature-flags';
import config from '../config';

class App extends Component {
  constructor(props) {
    super(props);
    // save shop to state for unsubscribing from pubnub channels
    this.state = {
      shop: props.shop,
    };
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillMount() {
    this.fetch();
  }

  componentDidMount() {
    this.initNotificationListener();
    EventEmitter.prototype.on('change-shop', this.fetch);
    this.props.getShopMerchantInfo(this.props.shop);
  }

  componentWillUnmount() {
    // use shop from state, this.props.shop is already updated at this point
    unsubscribe(`shop_${this.state.shop}`);
    EventEmitter.prototype.removeListener('change-shop', this.fetch);
  }

  fetch = () => {
    const { shop, fetchLoyalty, fetchDiscounts, fetchBarbers, fetchTimer } = this.props;

    fetchBarbers(shop);
    this.props.fetchShopActiveRegister(shop);
    fetchDiscounts();
    fetchLoyalty();
    fetchTimer();
  };

  initNotificationListener = () => {
    subscribe({
      channel: `shop_${this.props.shop}`,
      message: ({ message }) => {
        this.appointments(message);
        this.barbers(message);
        this.queueJobs(message);
        this.timeTrack(message);
        this.waitingList(message);
        this.campaign(message);
      },
    });
  };

  matches = (args) => {
    this.props.initializeMatches(this.props.brandId, args, this.props.shop);
  };

  queueJobs = (args) => {
    const finishedJob = get(args, 'queues.finished');
    const failedJob = get(args, 'queues.failed');

    const job = finishedJob || failedJob;

    if (job) {
      this.props.getJobsStatus(job.queueName, job.jobName, { shopId: this.props.shop });
    }
  };

  appointments = (args) => {
    const booked = get(args, 'appointments.booked');
    const updated = get(args, 'appointments.updated');
    const canceled = get(args, 'appointments.canceled');

    if (booked) {
      this.props.fetchAppointment(booked, 'booked');
    }

    if (updated) {
      this.props.fetchAppointment(updated, 'updated');
    }

    if (canceled) {
      this.props.fetchAppointment(canceled, 'canceled');
    }
  };

  barbers = (args) => {
    const updated = get(args, 'barbers.updated');
    if (updated) {
      this.props.fetchBarber(updated);
    }
  };

  // eslint-disable-next-line class-methods-use-this
  openConcierge = async () => {
    const chatManager = await getConciergeManager();
    chatManager.open();
  };

  timeTrack = (args) => {
    const update = get(args, 'timeTrack.toggled');
    if (update) {
      console.log('Pubnub: time track toggled [shop]', update);
      this.props.syncTimeTrack(update);
    }
  };

  waitingList = (args) => {
    const waitingListDate = get(args, 'waitingList.day');
    if (waitingListDate) {
      const updateList =
        this.props.shopOrBarber.type === 'shop'
          ? this.props.shopFetchWaitingListCustomers.bind(null, this.props.shopOrBarber.id, waitingListDate)
          : this.props.barberFetchWaitingListCustomers.bind(null, this.props.shopOrBarber.id, waitingListDate);
      updateList();
    }
  };

  campaign = (args) => {
    const campaign = get(args, 'campaign.updated');
    if (campaign) {
      this.props.fetchShopCampaign(this.props.shopOrBarber.id, campaign);
    }
  };

  render() {
    const { permissions, engageEnabled, featureFlags } = this.props;
    const shouldShowConcierge = !containerApp.isActive() && !this.props.isUserImpersonated;
    const barbersManagementEnabled = canUserViewBarbers(permissions);

    const data = [
      {
        id: 'appointments',
        text: t('nav.appointments'),
        iconName: GLYPHS.appointment,
        url: '/appointments',
        show: true,
      },
      {
        id: 'clients',
        text: t('nav.clients'),
        iconName: GLYPHS.customers,
        url: '/customers',
        show: canUserViewCustomers(permissions),
      },
      {
        id: 'barbers',
        text: t('nav.staff'),
        iconName: GLYPHS.barbers,
        url: barbersManagementEnabled ? '/barbers' : '/staff',
        show: barbersManagementEnabled || this.props.staffManagementEnabled,
      },
      {
        id: 'services',
        text: t('nav.services'),
        iconName: GLYPHS.services,
        url: '/services',
        show: canUserViewServices(permissions),
      },
      {
        id: 'products',
        text: t('nav.products'),
        iconName: GLYPHS.packages,
        url: '/products',
        show: canUserViewProducts(permissions),
      },
      {
        id: 'reports',
        text: t('nav.reports'),
        iconName: GLYPHS.reports,
        url: '/reports',
        show: canUserViewReports(permissions) && canUserViewDashboard(permissions),
      },
      {
        id: 'engage',
        text: t('nav.engage'),
        iconName: GLYPHS.engagement,
        url: '/engagement',
        show: engageEnabled && canUserViewEngage(permissions),
        label: {
          when: !this.props.frontendFlags.engagementOnboarded,
          text: t('nav.new_label'),
        },
      },
      this.props.clientReferralsButton,
      {
        id: 'help',
        text: t('nav.help_center'),
        iconName: GLYPHS.help,
        href: config.salesForceHelp.url,
        show: true,
        explainer: {
          when: this.props.firstLogin,
          component: FirstLoginWelcome,
        },
      },
      {
        id: 'settings',
        text: t('nav.settings'),
        iconName: GLYPHS.settings,
        url: '/settings',
        show: true,
      },
      {
        id: 'concierge',
        text: t('nav.chat_with_support'),
        iconName: GLYPHS.concierge,
        onClick: this.openConcierge,
        show: shouldShowConcierge,
      },
      this.props.loansButton,
      {
        id: 'memberships',
        text: t('nav.memberships'),
        iconName: GLYPHS.memberships,
        url: '/memberships',
        show: this.props.membershipsEnabled,
      },
      {
        id: 'referrals',
        text: t('nav.referrals'),
        iconName: GLYPHS.referral,
        href: 'https://getsqr.co/referral',
        linkAttrs: { target: '_blank' },
        show: true,
      },
      {
        id: 'phone-agent',
        text: 'Phone Agent',
        iconName: GLYPHS.phone,
        url: '/phone-agent',
        show: featureFlags.aiPhoneAssistant,
        label: {
          when: true,
          text: 'New',
        },
      },
    ];

    const className = classnames({
      collapse: this.props.collapsed,
      expand: !this.props.collapsed,
      container: containerApp.isActive(),
    });

    const loadingBarClassName = classnames('c-loading-bar', { 'is-hidden': containerApp.isActive() });

    return (
      <div>
        <LoadingBar className={loadingBarClassName} />
        <HandoffStatusBar />
        <Onboarding />
        <div>
          <Sidebar
            navData={data}
            collapsed={this.props.collapsed}
            setCollapsed={this.props.setCollapsed}
            display-if={!containerApp.isActive()}
          >
            <HeaderShopsContainer collapsed={this.props.collapsed && !this.props.sidebarActive} />
          </Sidebar>
          <ConciergeChat display-if={shouldShowConcierge} />
          <Main marginClass={className}>{this.props.children}</Main>
          <HandoffListener />
          <FixedButtonsContainer>
            <ChatButton
              setIsChatOpen={this.props.setIsChatOpen}
              chatUnreadMessagesCount={this.props.chatUnreadMessagesCount}
            />
            <RegisterButton display-if={canUserViewRegister(permissions)} url="/register" />
          </FixedButtonsContainer>
          <ChatPanel
            isChatOpen={this.props.isChatOpen}
            setIsChatOpen={this.props.setIsChatOpen}
            setUnreadMessagesCount={this.props.setUnreadMessagesCount}
            fetchAppointment={this.props.fetchAppointment}
          />
          <ShopViewPopups shopId={this.props.shop} getShopBankAccount={this.props.getShopBankAccount} />
        </div>
      </div>
    );
  }
}

App.propTypes = {
  brandId: PropTypes.string.isRequired,
  shop: PropTypes.string.isRequired,
  fetchBarbers: PropTypes.func.isRequired,
  fetchAppointment: PropTypes.func.isRequired,
  getJobsStatus: PropTypes.func.isRequired,
  children: PropTypes.element.isRequired,
  collapsed: PropTypes.bool.isRequired,
  setCollapsed: PropTypes.func.isRequired,
  permissions: PropTypes.object.isRequired,
  fetchBarber: PropTypes.func.isRequired,
  getShopMerchantInfo: PropTypes.func.isRequired,
  fetchShopActiveRegister: PropTypes.func.isRequired,
  fetchTimer: PropTypes.func.isRequired,
  fetchDiscounts: PropTypes.func.isRequired,
  fetchLoyalty: PropTypes.func.isRequired,
  isChatOpen: PropTypes.bool.isRequired,
  chatUnreadMessagesCount: PropTypes.number.isRequired,
  setUnreadMessagesCount: PropTypes.func.isRequired,
  setIsChatOpen: PropTypes.func.isRequired,
  firstLogin: PropTypes.bool.isRequired,
  sidebarActive: PropTypes.bool.isRequired,
  frontendFlags: PropTypes.shape({
    engagementOnboarded: PropTypes.bool,
    supplyOnboarded: PropTypes.bool,
  }).isRequired,
  syncTimeTrack: PropTypes.func.isRequired,
  shopFetchWaitingListCustomers: PropTypes.func.isRequired,
  barberFetchWaitingListCustomers: PropTypes.func.isRequired,
  shopOrBarber: PropTypes.object.isRequired,
  isUserImpersonated: PropTypes.bool.isRequired,
  loansButton: PropTypes.object.isRequired,
  membershipsEnabled: PropTypes.bool.isRequired,
  getShopBankAccount: PropTypes.func.isRequired,
  initializeMatches: PropTypes.func.isRequired,
  staffManagementEnabled: PropTypes.bool.isRequired,
  engageEnabled: PropTypes.bool.isRequired,
  fetchShopCampaign: PropTypes.func.isRequired,
  clientReferralsButton: PropTypes.object.isRequired,
  featureFlags: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  brandId: getShopBrandId(state),
  shop: state.auth.shop,
  permissions: state.auth.permissions,
  shopOrBarber: getRoleIdType(state),
  engageEnabled: canUseEngage(state),
  firstLogin: getFirstLogin(state),
  frontendFlags: getFrontendFlags(state),
  isUserImpersonated: state.auth.isUserImpersonated,
  membershipsEnabled: canActiveShopUseMemberships(state),
  staffManagementEnabled: canUserViewStaffManagement(state),
});

const mapDispatchToProps = {
  fetchBarbers: getBarbers,
  fetchAppointment: getAppointment,
  fetchBarber: barberUpdated,
  getShopMerchantInfo,
  fetchShopActiveRegister,
  fetchShopDiscounts,
  fetchBarberDiscounts,
  fetchLoyaltyAction: loyaltyActions.fetchLoyalty,
  getJobsStatus,
  shopFetchWaitingListCustomers,
  barberFetchWaitingListCustomers,
  getShopBankAccount,
  initializeMatches: initializeEcommerceMatches,
  fetchShopCampaign,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps((props) => ({
    fetchDiscounts:
      props.shopOrBarber.type === 'shop'
        ? props.fetchShopDiscounts.bind(null, props.shopOrBarber.id)
        : props.fetchBarberDiscounts.bind(null, props.shopOrBarber.id),

    fetchLoyalty: props.fetchLoyaltyAction.bind(null, props.shopOrBarber.id, props.shopOrBarber.type),
  })),
  withLoansSidebarButtons,
  withClientReferralsNavButton,
  withSidebarStateAndContext,
  withCollapsed,
  withIsChatOpen,
  withChatUnreadMessagesCount,
  shopAppFetchTimer,
  withTimeTrackingSync,
  withFeatureFlags
)(App);
